import { useEffect, useState } from 'react';

declare global {
  interface Window {
    DG: any;
  }
}

const LOADER_SRC = 'https://maps.api.2gis.ru/2.0/loader.js?pkg=full';
const LOAD_TIMEOUT_MS = 15000;

let loaderPromise: Promise<void> | null = null;

/**
 * Загружает loader.js 2GIS один раз на всё приложение и резолвится только
 * после DG.then(), то есть когда API реально готово к работе.
 *
 * Важно: window.DG выставляется загрузчиком синхронно, ещё до того как
 * появятся DG.map / DG.marker / DG.icon. Поэтому проверка `if (window.DG)`
 * сама по себе готовность не означает — ждём именно DG.then().
 *
 * Скрипт при размонтировании намеренно не удаляется: выгрузить глобал всё
 * равно нельзя, а removeChild ломает вторую карту, если она ещё ждёт загрузку.
 */
export const load2gisApi = (): Promise<void> => {
  if (loaderPromise) {
    return loaderPromise;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(
      () => reject(new Error('2GIS API не загрузился')),
      LOAD_TIMEOUT_MS
    );

    const waitForApi = () => {
      if (typeof window.DG?.then !== 'function') {
        window.clearTimeout(timeoutId);
        resolve();
        return;
      }

      window.DG.then(() => {
        window.clearTimeout(timeoutId);
        resolve();
      });
    };

    if (window.DG) {
      waitForApi();
      return;
    }

    const handleError = () => {
      window.clearTimeout(timeoutId);
      reject(new Error('Не удалось загрузить loader.js 2GIS'));
    };

    // Ловим в том числе скрипты, вставленные MapComponent и TaskStatusMap.
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${LOADER_SRC}"]`
    );

    if (existingScript) {
      existingScript.addEventListener('load', waitForApi);
      existingScript.addEventListener('error', handleError);
      return;
    }

    const script = document.createElement('script');
    script.src = LOADER_SRC;
    script.async = true;
    script.addEventListener('load', waitForApi);
    script.addEventListener('error', handleError);
    document.head.appendChild(script);
  });

  loaderPromise = promise.catch((error) => {
    loaderPromise = null; // разрешаем повторную попытку при следующем открытии
    throw error;
  });

  return loaderPromise;
};

/**
 * Возвращает true, когда API 2GIS готово и можно вызывать DG.map().
 */
export const use2gisMapReady = (): boolean => {
  const [isApiReady, setIsApiReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    load2gisApi()
      .then(() => {
        if (!cancelled) {
          setIsApiReady(true);
        }
      })
      .catch((error) => {
        console.error('2GIS:', error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return isApiReady;
};
